import { useAppState } from 'AppContextProvider'
import { ActionType, Selectors } from 'Reducers'
import { RouteNames, ONBOARDING_ROUTES_BY_STEP_ID } from 'RouteNames'
import { useDispatch, useSelector } from 'react-redux'
import { useHistory } from 'react-router-dom'
import styled from 'styled-components'
import i18n, { t } from 'i18next'
import { FirebaseError } from '@firebase/util'
import { useEffect, useState } from 'react'
import { getUid } from 'Services'
import { showToast } from 'Components/Toast'
import { getCookiesMap, getCurrency, getEnvironment, getFbc, getFbp, getInitialSearchPath, getMetaPurchaseEventId, getSanitizedMarketingSearchParams, getSearchParams, SENSITIVE_URL_PARAMS } from 'Lib'
import { CookieCategory } from 'Services/Cookie.service'

const DevelopmentPanelWrapper = styled.div`
  @media(max-width: 768px) {
    flex-direction: column;
    gap: 100px;
  }
`

const ToggleButton = styled.button`
  position: fixed;
  bottom: 10px;
  right: 10px;
  background-color: #b7d7ff;
  border: none;
  border-radius: 50%;
  width: 36px;
  height: 36px;
  font-size: 18px;
  font-weight: bold;
  color: #333;
  cursor: pointer;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
  z-index: 9999;
  display: flex;
  align-items: center;
  justify-content: center;

  @media(max-width: 768px) {
    width: 32px;
    height: 32px;
    font-size: 16px;
  }
`

const ButtonsWrapper = styled.div<{ $isExpanded: boolean }>`
  position: fixed;
  bottom: 55px;
  right: 10px;
  display: ${props => props.$isExpanded ? 'flex' : 'none'};
  flex-direction: column;
  gap: 6px;
  background: white;
  padding: 10px;
  border-radius: 8px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.15);
  z-index: 9998;
`

const Button = styled.button`
  background-color: #b7d7ff;
  border: none;
  border-radius: 3px;
  padding: 6px 10px;
  font-size: 13px;
  font-weight: bold;
  color: #333;
  cursor: pointer;
  box-shadow: 0 0 2px 0 rgba(0, 0, 0, 0.15);
  white-space: nowrap;
`

const Text = styled.p`
  align-self: center;
  font-size: 11px;
  margin: 2px 0;
  color: #666;
`

const InspectorWrapper = styled.div`
  position: fixed;
  bottom: 10px;
  left: 10px;
  display: flex;
  flex-direction: column;
  gap: 2px;
  background: white;
  padding: 10px 12px;
  border-radius: 8px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.15);
  z-index: 9998;
  width: min(340px, 92vw);
  max-height: 70vh;
  overflow-y: auto;
  font-size: 11px;
  color: #333;
`

const InspectorHeading = styled.div`
  font-weight: bold;
  font-size: 12px;
  margin: 8px 0 2px;
`

const InspectorRow = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 8px;
  font-family: monospace;
  word-break: break-all;
`

const InspectorLabel = styled.span`
  color: #666;
  flex-shrink: 0;
`

const InspectorValue = styled.span<{ $ok?: boolean, $bad?: boolean }>`
  font-weight: bold;
  text-align: right;
  color: ${props => props.$bad ? '#c0392b' : props.$ok ? '#1e7e34' : '#333'};
`

const InspectorHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  position: sticky;
  top: -10px;
  background: white;
  padding: 4px 0;
  margin-bottom: 2px;
  border-bottom: 1px solid #eee;
`

const InspectorTitle = styled.div`
  font-weight: bold;
  font-size: 12px;
`

const HeaderActions = styled.div`
  display: flex;
  gap: 4px;
`

const IconButton = styled.button`
  border: none;
  background: #eef4ff;
  border-radius: 4px;
  width: 22px;
  height: 22px;
  font-size: 13px;
  line-height: 1;
  cursor: pointer;
  color: #333;
`

const SummaryBar = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  margin-bottom: 4px;
`

const Chip = styled.span<{ $tone: 'ok' | 'bad' | 'neutral' }>`
  font-size: 10px;
  font-weight: bold;
  padding: 2px 6px;
  border-radius: 10px;
  color: ${props => props.$tone === 'bad' ? '#fff' : props.$tone === 'ok' ? '#0f5132' : '#555'};
  background: ${props => props.$tone === 'bad' ? '#c0392b' : props.$tone === 'ok' ? '#d1e7dd' : '#eee'};
`

const ChipRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
`

const ChipButton = styled.button`
  border: none;
  background: #b7d7ff;
  border-radius: 12px;
  padding: 3px 8px;
  font-size: 11px;
  cursor: pointer;
  color: #333;
  white-space: nowrap;
`

const CollapsibleHeading = styled.button`
  text-align: left;
  border: none;
  background: none;
  font-weight: bold;
  font-size: 12px;
  margin: 8px 0 2px;
  padding: 0;
  cursor: pointer;
  color: #333;
`

// Single source of truth shared with sanitizeSearchParams (the real security
// boundary) so the leak-check can't drift from what's actually stripped.
const SENSITIVE_KEYS = SENSITIVE_URL_PARAMS

// Never render/copy a raw secret value (screenshots, ticket pastes). Show only
// that the value is recoverable + its length.
const maskValue = (key: string, value: string): string =>
  SENSITIVE_KEYS.includes(key) ? `••• (${value.length} chars)` : value

const paramsToMaskedEntries = (params: URLSearchParams): Array<[string, string]> =>
  Array.from(params.entries()).map(([key, value]) => [key, maskValue(key, value)])

// _ttp / _tt_enable_cookie / ttcsid are the TikTok set. They must read "awaiting
// consent" until targeting is granted — the SDK is only loaded on grant precisely
// because ttq.disableCookie() writes _tt_enable_cookie=0 rather than removing it,
// and never touches ttcsid at all (see Services/TikTok.service.ts).
const PIXEL_COOKIES = ['_fbc', '_fbp', '_cbc', '_cbp', '_ttp', '_tt_enable_cookie', 'ttcsid'] as const
const CLICK_COOKIES: readonly string[] = ['_fbc', '_cbc'] // need a click-ID to exist
const PROD_ONLY_COOKIES: readonly string[] = ['ttcsid'] // CSID is off on the dev pixel

type CookieTone = 'ok' | 'bad' | 'neutral'

// fb.<subdomainIndex>.<timestampMs>.<clickId> — surface the stamped click-ID +
// time so QA can spot a stale re-stamp (the over-attribution case this PR fights).
const decodeClickCookie = (raw: string | null, urlClickId: string | null) => {
  if (!raw) return null
  const parts = raw.split('.')
  const tsMs = Number(parts[2])
  const clickId = parts.slice(3).join('.')
  return {
    clickId,
    stampedAt: Number.isFinite(tsMs) ? new Date(tsMs).toISOString().replace('T', ' ').slice(0, 19) : 'n/a',
    matchesUrl: urlClickId == null ? 'no url click-id' : (clickId === urlClickId ? 'matches url ✓' : 'differs from url ⚠')
  }
}

// Roll the per-cookie matchesUrl verdicts into one top-line chip. This is the
// headline proof of the current-visit-URL fix: the click stamped into _fbc/_cbc
// must still match the URL's click-ID — a mismatch means the Pixel re-stamped a
// stale click with a fresh timestamp (the over-attribution case). Worst case
// wins, so a single ⚠ surfaces even when the other cookie matches.
const restampSummary = (decoded: Array<ReturnType<typeof decodeClickCookie>>): { text: string, tone: CookieTone } => {
  const verdicts = decoded.flatMap(d => d ? [d.matchesUrl] : [])
  if (verdicts.some(v => v.includes('⚠'))) return { text: 'stale stamp ⚠', tone: 'bad' }
  if (verdicts.some(v => v.includes('✓'))) return { text: 'stamp matches ✓', tone: 'ok' }
  return { text: 'no click stamp', tone: 'neutral' }
}

// One-tap QA scenarios — land on HOME (the real ad-entry path) with a curated
// query so QA doesn't hand-type long URLs on a phone/staging.
const QA_SCENARIOS: Array<{ label: string, query: string }> = [
  { label: '📣 marketing', query: 'fbclid=FBQA&gclid=GQA&gbraid=GBQA&utm_source=qa&utm_campaign=qa_test' },
  { label: '🔒 secrets', query: 'fbclid=FBQA&token=QATOKEN&payment_intent_client_secret=QASECRET&subscriptionId=QASUB' },
  { label: '🇩🇪 lang=de', query: 'lang=de&fbclid=FBQA' },
  { label: '⏲ discount', query: 'dc=true&fbclid=FBQA' },
  { label: '👥 sgroup', query: 'sgroup=qa_group&fbclid=FBQA' },
  { label: '🌱 organic', query: '' }
]

const collectTrackingDiagnostics = () => {
  const cookies = getCookiesMap()
  const urlParams = new URLSearchParams(window.location.search)
  const storedParams = new URLSearchParams(localStorage.getItem('searchParams') ?? '')
  const w = window as any
  const consentState = window.bfConsent?.get()
  const consentGroups: string[] = consentState?.groups ?? []
  const targetingConsent = consentGroups.includes(CookieCategory.TARGETING)
  const fbclidInUrl = urlParams.get('fbclid')

  // localStorage − URL: params sanitizeUrl stripped from the address bar but
  // kept recoverable in storage (auth tokens, Stripe secrets…). This is the
  // security boundary made visible.
  const strippedFromUrl = Array.from(storedParams.entries())
    .filter(([key]) => !urlParams.has(key))
    .map(([key, value]): [string, string] => [key, maskValue(key, value)])

  // Explain WHY a cookie is missing instead of a bare "missing" — consent not
  // granted yet vs no click-ID present vs genuinely-should-be-there-but-isn't.
  const cookieStatus = (name: typeof PIXEL_COOKIES[number]): { text: string, tone: CookieTone } => {
    if (cookies.get(name)) return { text: '✓ set', tone: 'ok' }
    if (!targetingConsent) return { text: 'awaiting consent', tone: 'neutral' }
    if (CLICK_COOKIES.includes(name) && !fbclidInUrl) return { text: 'no click-id (n/a)', tone: 'neutral' }
    if (PROD_ONLY_COOKIES.includes(name) && getEnvironment() !== 'production') return { text: 'prod pixel only (n/a)', tone: 'neutral' }
    return { text: '⚠ missing (consent ok)', tone: 'bad' }
  }

  const decoded = {
    _fbc: decodeClickCookie(cookies.get('_fbc') ?? null, fbclidInUrl),
    _cbc: decodeClickCookie(cookies.get('_cbc') ?? null, fbclidInUrl)
  }

  return {
    environment: getEnvironment(),
    path: window.location.pathname,
    rawUrlSearch: window.location.search || '(empty)',
    leakedInUrl: SENSITIVE_KEYS.filter(key => urlParams.has(key)),
    storedSearchParams: localStorage.getItem('searchParams') ?? '(none)',
    strippedFromUrl,
    getSearchParams: paramsToMaskedEntries(getSearchParams()),
    marketingParams: paramsToMaskedEntries(getSanitizedMarketingSearchParams()),
    consent: {
      targeting: targetingConsent,
      groups: consentGroups.join(',') || '(none)',
      state: consentState ? `${consentState.status} · ${consentState.profile ?? 'no profile'}` : '(consent.js missing)'
    },
    pixelsLoaded: { metaSignals: typeof w.fbq === 'function', gtag: typeof w.gtag === 'function' },
    cookiesSet: PIXEL_COOKIES.filter(name => !!cookies.get(name)).length,
    recovery: {
      initialSearch: getInitialSearchPath() || '(empty)',
      originalSearchParams: localStorage.getItem('originalSearchParams') ?? '(none)'
    },
    resolved: {
      lang: i18n.language || '(unset)',
      currency: getCurrency() ?? '(default)',
      promoGroup: localStorage.getItem('sgroup') ?? '(default)',
      discountTimer: localStorage.getItem('dc') ?? '(off)',
      metaEventId: getMetaPurchaseEventId() ?? '(none yet)'
    },
    cookieStatuses: Object.fromEntries(PIXEL_COOKIES.map(name => [name, cookieStatus(name)])) as Record<string, { text: string, tone: CookieTone }>,
    decoded,
    restamp: restampSummary([decoded._fbc, decoded._cbc]),
    serverCapi: { getFbc: getFbc(), getFbp: getFbp() }
  }
}

const selections = {
  fasting_knowledge: 'beginner',
  goals: ['lose_weight'],
  perfect_weight: { inKg: { unit: 'cm/kg', value: 60 }, original: { unit: 'cm/kg', value: 60 } },
  weight: { inKg: { unit: 'cm/kg', value: 70 }, original: { unit: 'cm/kg', value: 70 } },
  height: { inCm: { value: 170 }, original: { unit: 'cm/kg', value: 170 } },
  gender: 'm',
  age: 25
}

const DevelopmentPanel: React.FC = ({ children }) => {
  const history = useHistory()
  const { setAppState } = useAppState()
  const dispatch = useDispatch()
  const user = useSelector(Selectors.getUser)
  const [uid, setUid] = useState<string | null>(getUid())
  const [isHidden, setIsHidden] = useState<boolean>(false)
  const [isExpanded, setIsExpanded] = useState<boolean>(false)
  // persisted so the inspector stays open across a scenario-launch full reload
  const [showInspector, setShowInspector] = useState<boolean>(() => localStorage.getItem('qaInspectorOpen') === '1')
  // bump to re-read cookies/URL on demand (pixel cookies populate async after consent)
  const [inspectorTick, setInspectorTick] = useState<number>(0)
  // verbose param/recovery/CAPI rows are collapsed by default to keep it scannable
  const [showDetails, setShowDetails] = useState<boolean>(false)
  const [simulateRenderCrash, setSimulateRenderCrash] = useState<boolean>(false)

  const toggleInspector = () => setShowInspector(prev => {
    const next = !prev
    localStorage.setItem('qaInspectorOpen', next ? '1' : '0')
    return next
  })

  // Land on HOME (the real ad-entry path) with the scenario's query so it runs
  // through the HOME->LOADING redirect + sanitizeUrl exactly like a real visit.
  const launchScenario = (query: string) => {
    window.location.href = `${window.location.origin}/${query ? `?${query}` : ''}`
  }

  useEffect(() => {
    setUid(getUid())
  }, [user])

  // After all hooks. Throwing during render exercises the top-level
  // Sentry.ErrorBoundary fallback (ErrorScreen); the Reload-page CTA recovers.
  if (simulateRenderCrash) {
    throw new Error('Simulated render crash (debug panel)')
  }

  // recomputed each render; inspectorTick forces a fresh read when QA hits refresh
  const diagnostics = showInspector ? collectTrackingDiagnostics() : null
  void inspectorTick

  const copyDiagnostics = () => {
    void navigator.clipboard
      .writeText(JSON.stringify(collectTrackingDiagnostics(), null, 2))
      .then(() => showToast('success', 'Diagnostics copied'))
      .catch(() => showToast('error', 'Clipboard copy failed'))
  }

  const signInUserAndSaveData = (resolve: () => void) => {
    dispatch({
      type: ActionType.ANONYMOUS_SIGN_IN_REQUEST,
      newsletterAccepted: true, // Only for logging
      reject: (e: FirebaseError) => {
        const errorMessage = t(`FirebaseError.${e.code}`, { ns: 'translation', defaultValue: t('FirebaseError.auth/unknown', { ns: 'translation' }) })

        showToast('error', errorMessage)
      },
      resolve: () => {
        saveOnboardingData(resolve)
      }
    })
  }

  const saveOnboardingData = (resolve: () => void) => {
    dispatch({
      type: ActionType.SAVE_ONBOARDING_DATA_REQUEST,
      onboardingData: { ...selections },
      newsletterAccepted: true,
      email: `test+${Math.round(Math.random() * 1000000)}@test.com`,
      resolve,
      reject: (e: FirebaseError) => {
        const errorMessage = t(`FirebaseError.${e.code}`, { ns: 'translation', defaultValue: t('FirebaseError.auth/unknown', { ns: 'translation' }) })

        showToast('error', errorMessage)
      }
    })
  }

  const skipToPayment = () => {
    setAppState((prevState) => ({
      ...prevState,
      selections
    }))

    signInUserAndSaveData(() => {
      dispatch({
        type: ActionType.HANDLE_NAVIGATING_TO_POST_ONBOARDING_ROUTE
      })
    })
  }

  const skipToEmailInput = () => {
    setAppState((prevState) => ({
      ...prevState,
      selections
    }))

    history.push(ONBOARDING_ROUTES_BY_STEP_ID.email)
  }

  const skipToRegistration = () => {
    signInUserAndSaveData(() => {
      history.push(`${RouteNames.UPGRADE_ACCOUNT}`)
    })
  }

  const logout = () => {
    setAppState((prevState) => ({
      ...prevState,
      selections: undefined
    }))
    dispatch({ type: ActionType.LOG_OUT_REQUEST, nextRouteName: RouteNames.LOADING })
  }

  // Contained by takeLatestSafe: expect a web_sagaCrashError report while the
  // app keeps reacting — nothing visible breaks.
  const simulateSagaCrash = () => {
    dispatch({ type: ActionType.SIMULATE_SAGA_CRASH })
    showToast('success', 'Saga crash dispatched — app must keep working (see console/Sentry)')
  }

  const onHide = () => {
    setIsHidden(true)
  }

  return (
    <>
      {!isHidden && (
        <DevelopmentPanelWrapper>
          {children}
          <ToggleButton onClick={() => setIsExpanded(!isExpanded)} title="Debug Panel">
            {isExpanded ? '✕' : '⚙'}
          </ToggleButton>
          <ButtonsWrapper $isExpanded={isExpanded}>
            {/* state-mutating shortcuts stay dev-only; staging shows just the read-only inspector */}
            {__DEV__ && (
              <>
                <Button onClick={skipToPayment}>payment</Button>
                <Button onClick={skipToEmailInput}>email (create user)</Button>
                <Button onClick={skipToRegistration}>registration</Button>
                <Button onClick={logout}>logout</Button>
                <Button onClick={simulateSagaCrash}>💣 saga crash</Button>
                <Button onClick={() => setSimulateRenderCrash(true)}>💥 render crash</Button>
              </>
            )}
            <Button onClick={toggleInspector}>
              {showInspector ? 'hide tracking' : 'tracking inspector'}
            </Button>
            <Button onClick={onHide}>hide</Button>
            <Text>{uid ?? 'No user'}</Text>
            <Text>{`v${__APP_VERSION__}`}</Text>
          </ButtonsWrapper>
          {diagnostics && (
            <InspectorWrapper>
              <InspectorHeader>
                <InspectorTitle>🔎 tracking · {diagnostics.environment}</InspectorTitle>
                <HeaderActions>
                  <IconButton title="refresh" onClick={() => setInspectorTick(prev => prev + 1)}>↻</IconButton>
                  <IconButton title="copy diagnostics" onClick={copyDiagnostics}>⧉</IconButton>
                  <IconButton title="close" onClick={toggleInspector}>✕</IconButton>
                </HeaderActions>
              </InspectorHeader>

              <SummaryBar>
                <Chip $tone={diagnostics.leakedInUrl.length ? 'bad' : 'ok'}>
                  {diagnostics.leakedInUrl.length ? `leak: ${diagnostics.leakedInUrl.join(', ')}` : 'no leak ✓'}
                </Chip>
                <Chip $tone={diagnostics.cookiesSet === PIXEL_COOKIES.length ? 'ok' : diagnostics.cookiesSet === 0 ? 'bad' : 'neutral'}>
                  {`cookies ${diagnostics.cookiesSet}/${PIXEL_COOKIES.length}`}
                </Chip>
                <Chip $tone={diagnostics.consent.targeting ? 'ok' : 'neutral'}>
                  {diagnostics.consent.targeting ? 'consent ✓' : 'no consent'}
                </Chip>
                <Chip $tone={diagnostics.restamp.tone}>
                  {diagnostics.restamp.text}
                </Chip>
              </SummaryBar>

              <InspectorHeading>🍪 Pixel cookies</InspectorHeading>
              {PIXEL_COOKIES.map(name => {
                const status = diagnostics.cookieStatuses[name]
                return (
                  <InspectorRow key={name}>
                    <InspectorLabel>{name}</InspectorLabel>
                    <InspectorValue $ok={status.tone === 'ok'} $bad={status.tone === 'bad'}>
                      {status.text}
                    </InspectorValue>
                  </InspectorRow>
                )
              })}
              {(['_fbc', '_cbc'] as const).map(name => {
                const decoded = diagnostics.decoded[name]
                if (!decoded) return null
                return (
                  <InspectorRow key={`${name}-decoded`}>
                    <InspectorLabel>{name} ↳</InspectorLabel>
                    <InspectorValue $bad={decoded.matchesUrl.includes('⚠')} $ok={decoded.matchesUrl.includes('✓')}>
                      {`${decoded.stampedAt} · ${decoded.matchesUrl}`}
                    </InspectorValue>
                  </InspectorRow>
                )
              })}

              <InspectorHeading>✅ consent / SDKs</InspectorHeading>
              <InspectorRow>
                <InspectorLabel>targeting consent</InspectorLabel>
                <InspectorValue $ok={diagnostics.consent.targeting} $bad={!diagnostics.consent.targeting}>
                  {diagnostics.consent.targeting ? 'granted ✓' : 'not granted'}
                </InspectorValue>
              </InspectorRow>
              <InspectorRow>
                <InspectorLabel>consent groups</InspectorLabel>
                <InspectorValue>{diagnostics.consent.groups}</InspectorValue>
              </InspectorRow>
              <InspectorRow>
                <InspectorLabel>consent state</InspectorLabel>
                <InspectorValue>{diagnostics.consent.state}</InspectorValue>
              </InspectorRow>
              <InspectorRow>
                <InspectorLabel>fbq (Meta/Signals)</InspectorLabel>
                <InspectorValue $ok={diagnostics.pixelsLoaded.metaSignals} $bad={!diagnostics.pixelsLoaded.metaSignals}>
                  {diagnostics.pixelsLoaded.metaSignals ? 'loaded ✓' : 'not loaded'}
                </InspectorValue>
              </InspectorRow>
              <InspectorRow>
                <InspectorLabel>gtag (Google)</InspectorLabel>
                <InspectorValue $ok={diagnostics.pixelsLoaded.gtag} $bad={!diagnostics.pixelsLoaded.gtag}>
                  {diagnostics.pixelsLoaded.gtag ? 'loaded ✓' : 'not loaded'}
                </InspectorValue>
              </InspectorRow>

              <InspectorHeading>🧪 QA scenarios</InspectorHeading>
              <ChipRow>
                {QA_SCENARIOS.map(scenario => (
                  <ChipButton key={scenario.label} onClick={() => launchScenario(scenario.query)}>
                    {scenario.label}
                  </ChipButton>
                ))}
              </ChipRow>

              <CollapsibleHeading onClick={() => setShowDetails(prev => !prev)}>
                {showDetails ? '▾' : '▸'} details — url · params · recovery · CAPI
              </CollapsibleHeading>
              {showDetails && (
                <>
                  <InspectorHeading>🔗 URL / recovery</InspectorHeading>
                  <InspectorRow>
                    <InspectorLabel>url.search</InspectorLabel>
                    <InspectorValue>{diagnostics.rawUrlSearch}</InspectorValue>
                  </InspectorRow>
                  <InspectorRow>
                    <InspectorLabel>INITIAL_SEARCH</InspectorLabel>
                    <InspectorValue>{diagnostics.recovery.initialSearch}</InspectorValue>
                  </InspectorRow>
                  <InspectorRow>
                    <InspectorLabel>localStorage</InspectorLabel>
                    <InspectorValue>{diagnostics.storedSearchParams}</InspectorValue>
                  </InspectorRow>
                  <InspectorRow>
                    <InspectorLabel>original (all-time)</InspectorLabel>
                    <InspectorValue>{diagnostics.recovery.originalSearchParams}</InspectorValue>
                  </InspectorRow>

                  <InspectorHeading>🧹 stripped from URL (recoverable)</InspectorHeading>
                  {diagnostics.strippedFromUrl.length === 0
                    ? <Text>(none)</Text>
                    : diagnostics.strippedFromUrl.map(([key, value]) => (
                      <InspectorRow key={key}>
                        <InspectorLabel>{key}</InspectorLabel>
                        <InspectorValue $ok>{value}</InspectorValue>
                      </InspectorRow>
                    ))}

                  <InspectorHeading>🧩 getSearchParams() (merged)</InspectorHeading>
                  {diagnostics.getSearchParams.length === 0
                    ? <Text>(none)</Text>
                    : diagnostics.getSearchParams.map(([key, value]) => (
                      <InspectorRow key={key}>
                        <InspectorLabel>{key}</InspectorLabel>
                        <InspectorValue>{value}</InspectorValue>
                      </InspectorRow>
                    ))}

                  <InspectorHeading>📣 marketing params (pixels)</InspectorHeading>
                  {diagnostics.marketingParams.length === 0
                    ? <Text>(none)</Text>
                    : diagnostics.marketingParams.map(([key, value]) => (
                      <InspectorRow key={key}>
                        <InspectorLabel>{key}</InspectorLabel>
                        <InspectorValue>{value}</InspectorValue>
                      </InspectorRow>
                    ))}

                  <InspectorHeading>🎛 resolved values</InspectorHeading>
                  <InspectorRow>
                    <InspectorLabel>lang (i18n)</InspectorLabel>
                    <InspectorValue>{diagnostics.resolved.lang}</InspectorValue>
                  </InspectorRow>
                  <InspectorRow>
                    <InspectorLabel>currency</InspectorLabel>
                    <InspectorValue>{diagnostics.resolved.currency}</InspectorValue>
                  </InspectorRow>
                  <InspectorRow>
                    <InspectorLabel>promoGroup</InspectorLabel>
                    <InspectorValue>{diagnostics.resolved.promoGroup}</InspectorValue>
                  </InspectorRow>
                  <InspectorRow>
                    <InspectorLabel>discount (dc)</InspectorLabel>
                    <InspectorValue>{diagnostics.resolved.discountTimer}</InspectorValue>
                  </InspectorRow>
                  <InspectorRow>
                    <InspectorLabel>metaEventId</InspectorLabel>
                    <InspectorValue>{diagnostics.resolved.metaEventId}</InspectorValue>
                  </InspectorRow>

                  <InspectorHeading>📤 server CAPI</InspectorHeading>
                  <InspectorRow>
                    <InspectorLabel>getFbc()</InspectorLabel>
                    <InspectorValue $ok={!!diagnostics.serverCapi.getFbc} $bad={!diagnostics.serverCapi.getFbc}>
                      {diagnostics.serverCapi.getFbc ?? 'null'}
                    </InspectorValue>
                  </InspectorRow>
                  <InspectorRow>
                    <InspectorLabel>getFbp()</InspectorLabel>
                    <InspectorValue $ok={!!diagnostics.serverCapi.getFbp} $bad={!diagnostics.serverCapi.getFbp}>
                      {diagnostics.serverCapi.getFbp ?? 'null'}
                    </InspectorValue>
                  </InspectorRow>
                </>
              )}
            </InspectorWrapper>
          )}
        </DevelopmentPanelWrapper>
      )}
    </>
  )
}

export default DevelopmentPanel
